import React, { useState, useEffect, useRef } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';

// Zajednički filter sheet za Zadatke i Intervencije.
// Mobitel: bottom sheet — zatvara se povlačenjem headera prema dolje ILI
//   povlačenjem tijela prema dolje kad je već skrolano na sam vrh (overscroll).
// Desktop: centrirani modal.
// Sekcije filtera se prosljeđuju kao children; footer prikazuje broj rezultata.
export default function FilterSheet({
  visible, onClose, isMobile,
  title = 'Filteri',
  totalActiveFilters = 0, resetFilters,
  resultCount = 0,
  resultNoun = { one: 'rezultat', few: 'rezultata', many: 'rezultata' },
  children,
}) {
  const [entered, setEntered] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const bodyRef = useRef(null);
  const hdrRef = useRef({ y: 0, active: false, cap: false, id: 0, el: null });
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Scroll-lock pozadine dok je sheet montiran
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Ulazna animacija (klizanje/fade nakon prvog framea)
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // ── Overscroll-drag tijela prema dolje (samo mobitel, samo s vrha) ──
  // Nativni listener s { passive: false } da možemo zaustaviti nativni scroll.
  useEffect(() => {
    if (!isMobile) return;
    const el = bodyRef.current;
    if (!el) return;
    let startY = 0;
    let active = false;
    const onStart = (e) => { startY = e.touches[0].clientY; active = false; };
    const onMove = (e) => {
      const dy = e.touches[0].clientY - startY;
      if (!active) {
        if (el.scrollTop <= 0 && dy > 4) { active = true; setDragging(true); }
        else return; // pusti nativni scroll
      }
      e.preventDefault();
      setDragY(Math.max(0, dy));
    };
    const onEnd = (e) => {
      if (!active) return;
      const dy = ((e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : startY)) - startY;
      active = false;
      setDragging(false);
      if (dy > 100) onCloseRef.current && onCloseRef.current();
      else setDragY(0);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [isMobile]);

  // ── Drag headera prema dolje (pointer; header ima touch-action: none) ──
  const onHdrDown = (e) => {
    if (!isMobile) return;
    hdrRef.current = { y: e.clientY, active: true, cap: false, id: e.pointerId, el: e.currentTarget };
  };
  const onHdrMove = (e) => {
    const h = hdrRef.current;
    if (!h.active) return;
    const dy = e.clientY - h.y;
    if (!h.cap) {
      if (dy <= 6) return;
      h.cap = true;
      setDragging(true);
      try { h.el.setPointerCapture(h.id); } catch { /* ignore */ }
    }
    setDragY(Math.max(0, dy));
  };
  const onHdrUp = (e) => {
    const h = hdrRef.current;
    if (!h.active) return;
    const dy = e.clientY - h.y;
    h.active = false;
    if (h.cap) { try { h.el.releasePointerCapture(h.id); } catch { /* ignore */ } }
    setDragging(false);
    if (dy > 100) onClose();
    else setDragY(0);
  };

  const nounFor = (n) => n === 1 ? resultNoun.one : (n >= 2 && n <= 4) ? resultNoun.few : resultNoun.many;

  const shown = entered && visible;

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
    display: 'flex', justifyContent: 'center',
    alignItems: isMobile ? 'flex-end' : 'center',
    padding: isMobile ? 0 : 16,
    opacity: shown ? 1 : 0,
    transition: 'opacity 0.2s ease',
  };
  const sheetStyle = isMobile
    ? {
        background: 'white', borderRadius: '20px 20px 0 0',
        display: 'flex', flexDirection: 'column', width: '100%', maxHeight: '85vh',
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -12px 40px rgba(0,0,0,0.25)',
        transform: `translateY(${shown ? dragY + 'px' : '100%'})`,
        transition: dragging ? 'none' : 'transform 0.26s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    : {
        background: 'white', borderRadius: 16,
        display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 560, maxHeight: '85vh',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.18s ease, transform 0.18s ease',
      };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={overlayStyle}>
      <div style={sheetStyle}>
        {/* Header (drag zona na mobitelu) */}
        <div
          onPointerDown={onHdrDown}
          onPointerMove={onHdrMove}
          onPointerUp={onHdrUp}
          onPointerCancel={onHdrUp}
          style={{ flexShrink: 0, padding: isMobile ? '10px 20px 12px' : '16px 20px', borderBottom: '1px solid #eef0f3', touchAction: isMobile ? 'none' : 'auto', userSelect: 'none', WebkitUserSelect: 'none', cursor: isMobile ? 'grab' : 'default' }}
        >
          {isMobile && <div style={{ width: 40, height: 4, borderRadius: 2, background: '#dfe6e9', margin: '0 auto 12px' }} />}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#1a2a4a', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <SlidersHorizontal size={17} /> {title}
            </span>
            <button onClick={onClose} title="Zatvori" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b2bec3', padding: 4, borderRadius: 6, display: 'flex' }}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          style={{ flex: 1, overflowY: 'auto', padding: 20, touchAction: isMobile ? 'pan-y' : 'auto', overscrollBehavior: 'contain' }}
        >
          {children}
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: '12px 20px', borderTop: '1px solid #eef0f3', display: 'flex', alignItems: 'center', gap: 12 }}>
          {totalActiveFilters > 0 && (
            <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: '#d63031', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: 0, whiteSpace: 'nowrap' }}>
              ✕ Očisti sve
            </button>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: '#1a2a4a', color: 'white', border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Prikaži {resultCount} {nounFor(resultCount)}
          </button>
        </div>
      </div>
    </div>
  );
}
